import React, { Component } from 'react';
import { bool, func, string, } from 'prop-types';
import MuiThemeProvider from 'material-ui/styles/MuiThemeProvider';
import getMuiTheme from "material-ui/styles/getMuiTheme";
import { DatePicker } from 'material-ui';

import moment from 'moment';

import cx from 'classnames';
import s from './DatePickerInput.css';

import portalTheme from "../../themes/portalTheme";
import startIcon from './icon-start-date.svg';
import endIcon from './icon-end-date.svg';


class DatePickerInput extends Component {
  static propTypes = {
    className: string,
    dateFormat: string,
    disabled: bool,
    hasInputBorder: bool,
    id: string.isRequired,
    isStartDate: bool,
    isEndDate: bool,
    maxDate: string,
    minDate: string,
    onChange: func,
    value: string,
  };

  static defaultProps = {
    dateFormat: 'YYYY-MM-DD',
  };

  static defaultState = {
    datePickerOpen: false,
    month: '',
    day: '',
    year: '',
  };

  constructor(props) {
    super(props);

    // Important: There should not be a reason to change this value.
    this.internalDateFormat = 'YYYY-MM-DD';

    // The IDs of the individual elements
    this.ids = {
      button: `${props.id}__open_date_picker_button`,
      datePicker: `${props.id}__date_picker`,
      yearInput: `${props.id}__year_input`,
      monthInput: `${props.id}__month_input`,
      dayInput: `${props.id}__day_input`,
    };

    this.state = this.buildStateFromProps(props);
  }

  componentWillReceiveProps = (nextProps) => this.setState(this.buildStateFromProps(nextProps));

  buildStateFromProps = props => {
    let state = DatePickerInput.defaultState;

    if (props.value && this.isValidDate(props.value)) {
      const { year, month, day } = this.extractDateValues(props.value);
      state = {...state, year, month, day};
    }

    if (props.minDate && this.isValidDate(props.minDate)) {
      const minDate = props.minDate;
      const minDateObject = this.extractDateObject(minDate);
      state = {...state, minDate, minDateObject};
    }
    if (props.maxDate && this.isValidDate(props.maxDate)) {
      const maxDate = props.maxDate;
      const maxDateObject = this.extractDateObject(maxDate);
      state = {...state, maxDate, maxDateObject};
    }

    return state;
  };


  parseDateString = (dateString) => moment(dateString, this.props.dateFormat);

  extractDateValues = dateString => {
    const date = this.parseDateString(dateString);
    return { month: date.format('MM'), day: date.format('DD'), year: date.format('YYYY') };
  };

  extractDateObject = dateString => this.parseDateString(dateString).toDate();

  buildDate = (year, month, day) => moment(`${year}-${month}-${day}`, this.internalDateFormat);

  buildDateObject = (year, month, day) => {
    const date = this.buildDate(year, month, day);
    if (date && date.isValid()) {
      return date.toDate();
    }
  };

  buildDateString = (year, month, day) => {
    const date = this.buildDate(year, month, day);
    if (date && date.isValid()) {
      return date.format(this.props.dateFormat);
    }
  };

  buildDateStringFromDateObject = dateObject => {
    const date = moment(dateObject);
    return date.format(this.props.dateFormat);
  };

  handleDatePickerChange = (alwaysNull, selectedDate) => {
    if (this.props.onChange) {
      const dateString = this.buildDateStringFromDateObject(selectedDate);

      this.props.onChange(dateString);
    }
  };

  handleInputChange = (year, month, day) => {
    if (month.length === 2 &&
        day.length === 2 &&
        year.length === 4 &&
        this.isValidDate(this.buildDateString(year, month, day)) &&
        this.props.onChange) {
      let selectedDate = this.buildDateString(year, month, day);
      if (this.isGreaterThanMaxDate(selectedDate)) {
        selectedDate = this.state.maxDate;
      } else if (this.isLessThanMinDate(selectedDate)) {
        selectedDate = this.state.minDate;
      }

      this.props.onChange(selectedDate);
    }
  };

  handleMonthChange = event => {
    const value = event.target.value;
    if (this.containsAlphaCharacter(value)) {
      return;
    }
    this.updateValue('month', value);
    if (value.length === 2) {
      this.handleInputChange(this.state.year, value, this.state.day);
      this.refs[this.ids.dayInput].focus();
    }
  };

  handleDayChange = event => {
    const value = event.target.value;
    if (this.containsAlphaCharacter(value)) {
      return;
    }
    this.updateValue('day', value);
    if (value.length === 2) {
      this.handleInputChange(this.state.year, this.state.month, value);
      this.refs[this.ids.yearInput].focus();
    }
  };

  handleYearChange = event => {
    const value = event.target.value;
    if (this.containsAlphaCharacter(value)) {
      return;
    }
    this.updateValue('year', value);
    if (value.length === 4) {
      this.handleInputChange(value, this.state.month, this.state.day);
      this.refs[this.ids.yearInput].blur();
    }
  };

  handleSingleDigitMonth = event => {
    let value = event.target.value;
    if (value.length === 1) {
      value = `0${value}`;
      this.setState({ month: value});
      this.handleInputChange(this.state.year, value, this.state.day);
    }
  };

  handleSingleDigitDay = event => {
    let value = event.target.value;
    if (value.length === 1) {
      value = `0${value}`;
      this.setState({ day: value});
      this.handleInputChange(this.state.year, this.state.month, value);
    }
  };

  handleComponentClick = () => this.refs[this.ids.monthInput].focus();

  // This prevents month from being focused on click of day or year inputs
  handleInputClick = event => event.stopPropagation();

  handleOpen = event => {
    event.preventDefault();
    event.stopPropagation();
    this.refs[this.ids.datePicker].focus();

    // I use a setTimeout because the dialog popover DOM element is dynamically created...I have to wait for a bit before manipulating it
    setTimeout(()=>{
      // The calendar dialog popover has no id, so I retrieve the DOM element via querySelector and the style attribute
      // this is super fragile
      let rootElement = document.querySelector('div[style="position: fixed; inset: 0px; z-index: 2000;"]')

      // this is a div which has the previous month arrow button, the month/year display, and next month arrow button
      let buttonsContainer = rootElement.querySelector('div[style="display: flex; justify-content: space-between; background-color: inherit; height: 48px;"]')
      let children = buttonsContainer.children;

      children[0].setAttribute("aria-label", "Previous month button")
      children[2].setAttribute("aria-label", "Next month button")
    }, "150")
  };

  handleInputFocus = event => event.target.select();

  updateValue = (field, value) => this.setState({ [field]: value });

  containsAlphaCharacter = string => /[a-zA-Z]+/.exec(string);

  isValidDate = (dateString) => {
    const date = this.parseDateString(dateString);
    return date && date.isValid();
  };

  isLessThanMinDate = dateString => {
    if (this.state.minDate) {
      const dateObject = this.extractDateObject(dateString);
      return dateObject.getTime() < this.state.minDateObject.getTime();
    }
    return false;
  };

  isGreaterThanMaxDate = dateString => {
    if (this.state.maxDate) {
      const dateObject = this.extractDateObject(dateString);
      return dateObject.getTime() > this.state.maxDateObject.getTime();
    }
    return false;
  };

  render() {
    let icon, iconAltText;
    if (this.props.isStartDate) {
      icon = startIcon;
      iconAltText = 'start date icon';
    } else if (this.props.isEndDate) {
      icon = endIcon;
      iconAltText = 'end date icon';
    } else {
      icon = startIcon;
      iconAltText = 'date icon';
    }

    const inputBorderClass = this.props.hasInputBorder ? s.inputBorder : '';
    const disabledClass = this.props.disabled ? s.disabled : '';

    return (
      <div
        className={cx(s.component, inputBorderClass, disabledClass, this.props.className)}
        id={this.props.id}
        onClick={this.handleComponentClick}
      >
        <button
          className={s.datePickerIconBtn}
          disabled={this.props.disabled}
          id={this.ids.button}
          onClick={this.handleOpen}
          type="button"
        >
          <img className={s.datePickerIcon} src={icon} alt={iconAltText}/>
        </button>
        <input
          className={cx(s.input, s.mm)}
          disabled={this.props.disabled}
          id={this.ids.monthInput}
          maxLength="2"
          onBlur={this.handleSingleDigitMonth}
          onChange={this.handleMonthChange}
          onClick={this.handleInputClick}
          onFocus={this.handleInputFocus}
          placeholder="mm"
          ref={this.ids.monthInput}
          type="text"
          value={this.state.month}
          title="month"
        />
        <span className={s.slash}>/</span>
        <input
          className={cx(s.input, s.dd)}
          disabled={this.props.disabled}
          id={this.ids.dayInput}
          maxLength="2"
          onBlur={this.handleSingleDigitDay}
          onChange={this.handleDayChange}
          onClick={this.handleInputClick}
          onFocus={this.handleInputFocus}
          placeholder="dd"
          ref={this.ids.dayInput}
          type="text"
          value={this.state.day}
          title="day"
        />
        <span className={s.slash}>/</span>
        <input
          className={cx(s.input, s.yyyy)}
          disabled={this.props.disabled}
          id={this.ids.yearInput}
          maxLength="4"
          onChange={this.handleYearChange}
          onClick={this.handleInputClick}
          onFocus={this.handleInputFocus}
          placeholder="yyyy"
          ref={this.ids.yearInput}
          type="text"
          value={this.state.year}
          title="year"
        />
        <MuiThemeProvider muiTheme={getMuiTheme(portalTheme)}>
            <DatePicker
                container="inline"
                disabled={this.props.disabled}
                id={this.ids.datePicker}
                title="datepicker"
                maxDate={this.state.maxDateObject}
                minDate={this.state.minDateObject}
                onChange={this.handleDatePickerChange}
                ref={this.ids.datePicker}
                style={{height: '100%', position: 'absolute', top: 0}}
                textFieldStyle={{height: '100%', width: 0}}
                value={this.buildDateObject(this.state.year, this.state.month, this.state.day)}
            />
        </MuiThemeProvider>
      </div>
    );
  }
}

export default DatePickerInput;



// WEBPACK FOOTER //
// ./src/components/DatePickerInput/DatePickerInput.js